import React, { useState, useEffect } from 'react';
import { propOr, path, isEmpty } from 'ramda';
import { useQuery, useMutation } from '@apollo/react-hooks';
import Loader from 'react-loader-spinner';
import {
  AppProvider,
  Button,
  Card,
  ButtonGroup,
  Form,
  FormLayout,
  Frame,
  Layout,
  Link,
  Page,
  Subheading,
  RangeSlider,
  Select,
  Modal,
  Stack,
  TextContainer,
  Heading,
  Icon,
  TextField,
  Badge,
  Toast,
} from '@shopify/polaris';
import {
  LockMinor,
  CashDollarMajorMonotone,
  ClockMajorMonotone,
  CustomersMajorMonotone,
  LocationMajorMonotone,
  DuplicateMinor,
} from '@shopify/polaris-icons';
import SuccessBanner from '../components/SuccessBanner';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { GET_CAREER_PORTAL, GET_CURRENT_SUBSCRIPTION } from '../apollo/queries';
import { CREATE_SHOP_CAREER_PORTAL, UPDATE_SHOP_CAREER_PORTAL } from '../apollo/mutation';
import { FONT_WEIGHT_OPTIONS, FONT_FAMILY_OPTIONS } from '../../utils/formDefaultValues';

import ColorPicker from '../components/ColorPicker';

import { defaultWhiteColor, defaultDarkGrayColor, defaultLightGrayColor, hslaColor, hexColor } from '../../utils/defaultColors';
import { keysToCamel } from '../../utils/normalizer';

const JobListingsSection = ({ history, shopifyToken, shopifyDomain }) => {
  const { push: changeRoute } = history;
  const queryAttr = { variables: { shopifyToken } };

  const [createShopCareerPortal] = useMutation(CREATE_SHOP_CAREER_PORTAL);
  const [updateShopCareerPortal] = useMutation(UPDATE_SHOP_CAREER_PORTAL);

  const { loading, data, refetch } = useQuery(GET_CAREER_PORTAL, queryAttr);
  const { data: subscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, queryAttr);

  const shop = propOr({}, 'shop')(subscriptionData);
  const currentSubscription = propOr({}, 'currentSubscription')(shop);
  const isSubscribedUser = !isEmpty(currentSubscription);
  const isPremiumUser = isSubscribedUser && currentSubscription.subscriptionType === 'Premium';
  const isGrowthUser = isSubscribedUser && currentSubscription.subscriptionType === 'Growth';
  const isBusinessUser = isSubscribedUser && currentSubscription.subscriptionType === 'Business';
  const canFilterUser = isPremiumUser || isGrowthUser || isBusinessUser;
  const careerPortalSet = propOr(false, 'careerPortalSet')(shop);
  const isCareerPortalEmpty = !careerPortalSet;
  const objectSnippet = `<object type="text/html" data="https://${shopifyDomain}/a/careers/widget" width="100%" height="700px"></object>`;
  const iframeSnippet = `<iframe src="https://${shopifyDomain}/a/careers/widget" width="100%" height="700px" />`;

  const objectSearchSnippet = `<object type="text/html" data="https://${shopifyDomain}/a/careers/searchbox" width="100%" height="40px"></object>`;
  const iframeSearchSnippet = `<iframe src="https://${shopifyDomain}/a/careers/searchbox" width="100%" height="40px" />`;

  const [form, setForm] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [isSaveBarActive, setIsSaveBarActive] = useState(false);
  const [toastStatus, setToast] = useState({ isOpen: false, text: '' });
  const togglePreviewModal = () => setPreview(!preview);

  useEffect(() => {
    const careerPortal = propOr({}, 'careerPortal')(data);
    const jobListHeadingText = propOr('', 'jobListHeadingText')(careerPortal);
    const styleControls = propOr({}, 'styleControls')(careerPortal);
    const jobListSection = propOr({}, 'job_list_section')(styleControls);
    const styleControlsFormatted = keysToCamel(careerPortal.styleControls);

    const jobListCategoryFilterEnabled = propOr(true, 'jobListCategoryFilterEnabled')(careerPortal);
    const jobListLocationFilterEnabled = propOr(true, 'jobListLocationFilterEnabled')(careerPortal);
    const jobListKeywordFilterEnabled = propOr(true, 'jobListKeywordFilterEnabled')(careerPortal);
    const jobListDepartmentFilterEnabled = propOr(true, 'jobListDepartmentFilterEnabled')(careerPortal);
    const jobListSortEnabled = propOr(true, 'jobListSortEnabled')(careerPortal);
    const talentCommunitySection = propOr(false, 'talentCommunitySection')(careerPortal);

    setForm((prevForm) => ({
      ...prevForm,
      ...styleControlsFormatted,
      jobListHeadingText,
      jobListCategoryFilterEnabled,
      jobListLocationFilterEnabled,
      jobListKeywordFilterEnabled,
      jobListDepartmentFilterEnabled,
      jobListSortEnabled,
      jobListSection: {
        ...prevForm.jobListSection,
        enabled: true,
        backgroundColor: jobListSection?.background_color || defaultWhiteColor,
        headingColor: jobListSection?.heading_color || defaultDarkGrayColor,
        headingWeight: jobListSection?.heading_weight || '700',
        headingSize: jobListSection?.heading_size || '26',
        headingFontFamily: jobListSection?.heading_font_family || 'inherit',
        jobListColor: jobListSection?.job_list_color || defaultWhiteColor,
        jobListHoverColor: jobListSection?.job_list_hover_color || defaultLightGrayColor,
        jobListTextColor: jobListSection?.job_list_text_color || defaultDarkGrayColor,
        jobListFontFamily: jobListSection?.job_list_font_family || 'inherit',
        applyButtonColor: jobListSection?.apply_button_color || defaultWhiteColor,
        applyButtonTextColor: jobListSection?.apply_button_text_color || defaultDarkGrayColor,
      },
      talentCommunityStyles: {
        ...styleControlsFormatted?.talentCommunitySection,
      },
      talentCommunitySection,
    }));
  }, [data]);

  if (loading || isLoading) {
    return (
      <div className='loader-container'>
        <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
      </div>
    );
  }

  const updateField = (fieldName) => (value) => {
    setIsSaveBarActive(true);
    setForm({
      ...form,
      [fieldName]: value,
    });
  };

  const updateColor = (fieldName) => (color) => {
    setIsSaveBarActive(true);
    setForm({
      ...form,
      jobListSection: {
        ...form.jobListSection,
        [fieldName]: color,
      },
    });
  };

  const updateFontValue = ({ sectionName, fieldName }) => (value) => {
    setIsSaveBarActive(true);
    setForm({
      ...form,
      [sectionName]: {
        ...form[sectionName],
        [fieldName]: `${value}`,
      },
    });
  };

  const toggleToast = (text) =>
    setToast((prev) => ({
      isOpen: !prev.isOpen,
      text: prev.isOpen ? prev.text : text,
    }));

  const onSuccess = () => {
    refetch();
    setIsSaveBarActive(false);
    setShowSuccessBanner(true);
    setIsLoading(false);
  };

  const onError = (e) => {
    let messages = path(['graphQLErrors', 0, 'message'])(e);
    toggleToast(`Error: ${messages}`);
    setIsLoading(false);
  };

  const onSubmit = (e) => {
    e?.preventDefault();
    setPreview(false);
    setIsLoading(true);
    const careerPortal = propOr({}, 'careerPortal')(data);
    const save = isCareerPortalEmpty ? createShopCareerPortal : updateShopCareerPortal;
    const variables = {
      ...careerPortal,
      ...form,
      shopifyToken,
    };
    delete variables.styleControls;

    save({ variables }).then(onSuccess).catch(onError);
  };

  const toastMarkup = toastStatus.isOpen ? <Toast content={toastStatus.text} onDismiss={toggleToast} /> : null;

  return (
    <AppProvider
      i18n={{
        Polaris: {
          DropZone: {
            actionTitle: 'Add image',
          },
        },
      }}>
      <Frame>
        <Page
        breadcrumbs={[{ content: 'Career Portal', onAction: () => { changeRoute('/career-portal-index'); } }]}
          title='Job listings section'
          titleMetadata={<Badge status={'success'}>Published</Badge>}
          pagination={{
            hasPrevious: !isSaveBarActive,
            hasNext: !isSaveBarActive,
            nextURL: !isSaveBarActive ? `/career-portal/talent-community-section` : null,
            previousURL: !isSaveBarActive ? `/career-portal/benefits-section` : null,
            nextTooltip: 'Next',
            previousTooltip: 'Previous',
          }}
          primaryAction={
            <ButtonGroup>
              <Button plain monochrome onClick={togglePreviewModal}>
                Preview
              </Button>
              <Button primary onClick={onSubmit} disabled={!isSaveBarActive}>
                Publish
              </Button>
            </ButtonGroup>
          }>
          <Form onSubmit={onSubmit}>
            {showSuccessBanner && (
              <SuccessBanner
                history={history}
                title='Job listings section updated'
                onDismiss={() => setShowSuccessBanner(false)}
                portalBody={'View on your career portal'}
                detailsBody={'go to all career portal sections'}
                portalUrl={`https://${shopifyDomain}/a/careers/`}
                detailsUrl={'/career-portal-index'}
              />
            )}
            <Layout>
              <Layout.AnnotatedSection title='Content' description='Customize the job listings section of your careers page.'>
                <Card sectioned>
                  <FormLayout>
                    <TextField
                      id='jobListHeadingText'
                      label='Title text'
                      value={form.jobListHeadingText}
                      name='jobListHeadingText'
                      onChange={updateField('jobListHeadingText')}
                    />
                  </FormLayout>
                  <br />
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                    }}>
                    {!canFilterUser && (
                      <div>
                        <Icon source={LockMinor} />
                      </div>
                    )}
                    <div style={{ flexGrow: 1, paddingLeft: '5px' }}>
                      <Subheading>Filtering & sorting</Subheading>
                      {!canFilterUser && (
                        <>
                          <Link onClick={() => changeRoute('/account-plan')} removeUnderline>
                            Upgrade account plan
                          </Link>
                          <br />
                        </>
                      )}
                    </div>
                  </div>
                  <br />
                  <>
                    <Card sectioned>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}>
                        <p>
                          Filters job by department is{' '}
                          <b>{form.jobListDepartmentFilterEnabled && canFilterUser ? 'enabled' : 'disabled'}</b>
                        </p>
                        <Button
                          primary={!form.jobListDepartmentFilterEnabled || !canFilterUser}
                          disabled={!canFilterUser}
                          onClick={() => updateField('jobListDepartmentFilterEnabled')(!form.jobListDepartmentFilterEnabled)}>
                          {form.jobListDepartmentFilterEnabled && canFilterUser ? 'Disable' : 'Enable'}
                        </Button>
                      </div>
                    </Card>
                    <Card sectioned>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}>
                        <p>
                          Filters job by category is{' '}
                          <b>{form.jobListCategoryFilterEnabled && canFilterUser ? 'enabled' : 'disabled'}</b>
                        </p>
                        <Button
                          primary={!form.jobListCategoryFilterEnabled || !canFilterUser}
                          disabled={!canFilterUser}
                          onClick={() => updateField('jobListCategoryFilterEnabled')(!form.jobListCategoryFilterEnabled)}>
                          {form.jobListCategoryFilterEnabled && canFilterUser ? 'Disable' : 'Enable'}
                        </Button>
                      </div>
                    </Card>
                    <Card sectioned>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}>
                        <p>
                          Filters job by location is{' '}
                          <b>{form.jobListLocationFilterEnabled && canFilterUser ? 'enabled' : 'disabled'}</b>
                        </p>
                        <Button
                          primary={!form.jobListLocationFilterEnabled || !canFilterUser}
                          disabled={!canFilterUser}
                          onClick={() => updateField('jobListLocationFilterEnabled')(!form.jobListLocationFilterEnabled)}>
                          {form.jobListLocationFilterEnabled && canFilterUser ? 'Disable' : 'Enable'}
                        </Button>
                      </div>
                    </Card>
                    <Card sectioned>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}>
                        <p>
                          Sort jobs by job title is <b>{form.jobListSortEnabled && canFilterUser ? 'enabled' : 'disabled'}</b>
                        </p>
                        <Button
                          primary={!form.jobListSortEnabled || !canFilterUser}
                          disabled={!canFilterUser}
                          onClick={() => updateField('jobListSortEnabled')(!form.jobListSortEnabled)}>
                          {form.jobListSortEnabled && canFilterUser ? 'Disable' : 'Enable'}
                        </Button>
                      </div>
                    </Card>
                    <Card sectioned>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}>
                        <p>
                          Search jobs by keyword is{' '}
                          <b>{form.jobListKeywordFilterEnabled && canFilterUser ? 'enabled' : 'disabled'}</b>
                        </p>
                        <Button
                          primary={!form.jobListKeywordFilterEnabled || !canFilterUser}
                          disabled={!canFilterUser}
                          onClick={() => updateField('jobListKeywordFilterEnabled')(!form.jobListKeywordFilterEnabled)}>
                          {form.jobListKeywordFilterEnabled && canFilterUser ? 'Disable' : 'Enable'}
                        </Button>
                      </div>
                    </Card>
                  </>
                </Card>
              </Layout.AnnotatedSection>
              <Layout.AnnotatedSection
                title='Appearance'
                description='Customize the font and section background colors to match your store’s theme.'>
                <Card sectioned>
                  <Card.Section title='SECTION'>
                    <ColorPicker
                      colorName='Background color'
                      defaultColor={form.jobListSection?.backgroundColor}
                      onSelectColor={updateColor('backgroundColor')}
                    />
                  </Card.Section>
                  <Card.Section title='TITLE TEXT'>
                    <ColorPicker
                      colorName='Title color'
                      defaultColor={form.jobListSection?.headingColor}
                      onSelectColor={updateColor('headingColor')}
                    />
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Title font size'
                        value={form.jobListSection?.headingSize}
                        onChange={updateFontValue({ sectionName: 'jobListSection', fieldName: 'headingSize' })}
                        output
                        max={72}
                        min={12}
                        suffix={`${form.jobListSection?.headingSize}px`}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'jobListSection', fieldName: 'headingWeight' })}
                        value={form.jobListSection?.headingWeight}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'jobListSection', fieldName: 'headingFontFamily' })}
                        value={form.jobListSection?.headingFontFamily}
                      />
                    </div>
                  </Card.Section>
                  <Card.Section title='JOB LISTING TEXT'>
                    <ColorPicker
                      colorName='Job listing color'
                      defaultColor={form.jobListSection?.jobListColor}
                      onSelectColor={updateColor('jobListColor')}
                    />
                    <ColorPicker
                      colorName='Job listing hover color'
                      defaultColor={form.jobListSection?.jobListHoverColor}
                      onSelectColor={updateColor('jobListHoverColor')}
                    />
                    <ColorPicker
                      colorName='Job listing text color'
                      defaultColor={form.jobListSection?.jobListTextColor}
                      onSelectColor={updateColor('jobListTextColor')}
                    />
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Job listing font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'jobListSection', fieldName: 'jobListFontFamily' })}
                        value={form.jobListSection?.jobListFontFamily}
                      />
                    </div>
                  </Card.Section>
                </Card>
              </Layout.AnnotatedSection>

              <Layout.AnnotatedSection
                title='Job list widget'
                description="Import your ShopHire job listing wherever your need, include the snippet source code and it's ready to go.">
                <Card sectioned>
                  <Card.Section title='SOURCE CODE'>
                    <br />
                    <Stack vertical>
                      <TextField
                        label={'Object tag'}
                        value={objectSnippet}
                        connectedRight={
                          <CopyToClipboard
                            text={objectSnippet}
                            onCopy={() => {
                              setToast({
                                isOpen: true,
                                text: 'Copied to clipboard',
                              });
                            }}>
                            <Button icon={DuplicateMinor} />
                          </CopyToClipboard>
                        }
                      />
                      <TextField
                        label={'Iframe tag'}
                        value={iframeSnippet}
                        connectedRight={
                          <CopyToClipboard
                            text={iframeSnippet}
                            onCopy={() => {
                              setToast({
                                isOpen: true,
                                text: 'Copied to clipboard',
                              });
                            }}>
                            <Button icon={DuplicateMinor} />
                          </CopyToClipboard>
                        }
                      />
                    </Stack>
                  </Card.Section>
                </Card>
              </Layout.AnnotatedSection>

              <Layout.AnnotatedSection
                title='Search bar widget'
                description="Import a ShopHire job searchbox wherever your need, include the snippet source code and it's ready to go.">
                <Card sectioned>
                  <Card.Section title='SOURCE CODE'>
                    <br />
                    <Stack vertical>
                      <TextField
                        label={'Object tag'}
                        value={objectSearchSnippet}
                        connectedRight={
                          <CopyToClipboard
                            text={objectSearchSnippet}
                            onCopy={() => {
                              setToast({
                                isOpen: true,
                                text: 'Copied to clipboard',
                              });
                            }}>
                            <Button icon={DuplicateMinor} />
                          </CopyToClipboard>
                        }
                      />
                      <TextField
                        label={'Iframe tag'}
                        value={iframeSearchSnippet}
                        connectedRight={
                          <CopyToClipboard
                            text={iframeSearchSnippet}
                            onCopy={() => {
                              setToast({
                                isOpen: true,
                                text: 'Copied to clipboard',
                              });
                            }}>
                            <Button icon={DuplicateMinor} />
                          </CopyToClipboard>
                        }
                      />
                    </Stack>
                  </Card.Section>
                </Card>
              </Layout.AnnotatedSection>
            </Layout>
            <div className='career-portal__form-buttons'>
              <Button onClick={() => changeRoute('/career-portal-index')}>Cancel</Button>
              <Button primary submit disabled={!isSaveBarActive}>
                Publish
              </Button>
            </div>
          </Form>
          <Modal
            open={!!preview}
            large
            onClose={togglePreviewModal}
            primaryAction={{
              content: 'Publish',
              disabled: !isSaveBarActive,
              onAction: () => {
                onSubmit();
              },
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: togglePreviewModal,
              },
            ]}
            title={
              <TextContainer>
                <Heading>Preview job listing section</Heading>
                <p style={{ fontSize: '0.85em' }}>
                  Your theme fonts are not applied in the preview bellow, publish the section to your career portal to see how the
                  section will look on your store
                </p>
              </TextContainer>
            }
            loading={false}>
            <div className={'preview__modal'}>
              <section
                className='job_list_section'
                style={{
                  backgroundColor: `${hslaColor(form.jobListSection?.backgroundColor, '#fff')}`,
                }}>
                <div className='wrapper'>
                  <h2
                    className='heading_text'
                    style={{
                      color: `${hslaColor(form.jobListSection?.headingColor, '#3a3a3a')}`,
                      fontSize: `${form.jobListSection?.headingSize || '26'}px`,
                      fontWeight: `${form.jobListSection?.headingWeight || '700'}`,
                    }}>
                    {form.jobListHeadingText || 'Open Roles'}
                  </h2>
                  <ul
                    className='jobs_table'
                    style={{
                      backgroundColor: `${hslaColor(form.jobListSection?.jobListColor, '#fff')}`,
                    }}>
                    {canFilterUser && (
                      <>
                        {form.jobListKeywordFilterEnabled !== false && (
                          <li className='jobs_table_filters jobs_table_filters_input'>
                            <div className='job-filter-input'>
                              <input type='text' id='job-keywords-filter' placeholder='Search job opportunities' />
                            </div>
                          </li>
                        )}
                        {(form.jobListCategoryFilterEnabled !== false ||
                          form.jobListLocationFilterEnabled !== false ||
                          form.jobListDepartmentFilterEnabled !== false ||
                          form.jobListSortEnabled !== false) && (
                          <li className='jobs_table_filters'>
                            {form.jobListDepartmentFilterEnabled !== false && (
                              <select className='filter-select' id='department-filter'>
                                <option value='all'>Department</option>
                              </select>
                            )}
                            {form.jobListCategoryFilterEnabled !== false && (
                              <select className='filter-select' id='category-filter'>
                                <option value='all'>Category</option>
                              </select>
                            )}
                            {form.jobListLocationFilterEnabled !== false && (
                              <select className='filter-select' id='location-filter'>
                                <option value='all'>Location</option>
                              </select>
                            )}
                            {form.jobListSortEnabled !== false && (
                              <select id='jobs-sort'>
                                <option value='az'>Title, A-Z</option>
                              </select>
                            )}
                          </li>
                        )}
                      </>
                    )}
                    {[{}, {}].map((el, index) => (
                      <li className='job_item result' key={`${index}`}>
                        <div className='job_item_content'>
                          <p
                            className='job_title'
                            style={{
                              color: `${hslaColor(form.jobListSection?.jobListTextColor, '#3a3a3a')}`,
                            }}>
                            Buyer
                          </p>
                          <div className='job_data'>
                            <div
                              className='job_location'
                              style={{
                                color: `${hslaColor(form.jobListSection?.jobListTextColor, '#3a3a3a')}`,
                              }}>
                              <Stack spacing={'tight'}>
                                <div style={{ fill: `${hexColor(form.jobListSection?.jobListTextColor, '#3a3a3a')}` }}>
                                  <Icon source={LocationMajorMonotone} />
                                </div>
                                <span>Houston, Texas</span>
                              </Stack>
                            </div>
                            <div
                              className='job_category'
                              style={{
                                color: `${hslaColor(form.jobListSection?.jobListTextColor, '#3a3a3a')}`,
                              }}>
                              <Stack spacing={'tight'}>
                                <div style={{ fill: `${hexColor(form.jobListSection?.jobListTextColor, '#3a3a3a')}` }}>
                                  <Icon source={CustomersMajorMonotone} />
                                </div>
                                <span>Merchandising </span>
                              </Stack>
                            </div>
                            <div
                              className='job_type'
                              style={{
                                color: `${hslaColor(form.jobListSection?.jobListTextColor, '#3a3a3a')}`,
                              }}>
                              <Stack spacing={'tight'}>
                                <div style={{ fill: `${hexColor(form.jobListSection?.jobListTextColor, '#3a3a3a')}` }}>
                                  <Icon source={ClockMajorMonotone} />
                                </div>
                                <span> Full-Time </span>
                              </Stack>
                            </div>
                            <div
                              className='job_payment'
                              style={{
                                color: `${hslaColor(form.jobListSection?.jobListTextColor, '#3a3a3a')}`,
                              }}>
                              <Stack spacing={'tight'}>
                                <div style={{ fill: `${hexColor(form.jobListSection?.jobListTextColor, '#3a3a3a')}` }}>
                                  <Icon source={CashDollarMajorMonotone} />
                                </div>
                                <span>$4000 per month</span>
                              </Stack>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <span className='shophire_pagination'>
                    <button
                      className='arrow back'
                      style={{
                        color: `${hslaColor(form.jobListSection?.jobListTextColor, '#3a3a3a')}`,
                      }}>
                      « Previous
                    </button>
                    <span
                      className='page_info'
                      style={{
                        color: `${hslaColor(form.jobListSection?.jobListTextColor, '#3a3a3a')}`,
                      }}>
                      Page 1 of 1
                    </span>
                    <button
                      className='arrow next'
                      style={{
                        color: `${hslaColor(form.jobListSection?.jobListTextColor, '#3a3a3a')}`,
                      }}>
                      Next »
                    </button>
                  </span>
                </div>
              </section>
            </div>
          </Modal>
        </Page>
        {toastMarkup}
      </Frame>
    </AppProvider>
  );
};

export default JobListingsSection;
